import { Fragment } from 'react';

/**
 * A report section's text with its placeholders filled (T-0245, E22 design
 * §5). The drafting prompts write `{{posture}}`, `{{readiness_total}}`,
 * `{{recommendation}}`, `{{criterion:<id>}}` and `{{domain:<id>}}` where a
 * rollup goes; the report read serves the word each one prints (built by
 * the API with the PDF renderer's own `placeholderValues`, so the tab and
 * the PDF say the same thing). Here they are only put in place — nothing is
 * formatted or computed. A placeholder no rollup answers prints an em dash,
 * as in the PDF; text without placeholders reads as written.
 *
 * Mirrors `packages/pdf-render/src/placeholders.ts → fillPlaceholders`.
 */

const TOKEN = /\{\{([^{}]*)\}\}/g;

export type ReportTextPart =
  | { kind: 'text'; text: string }
  /** `text` null: no rollup answers the placeholder. */
  | { kind: 'value'; key: string; text: string | null };

export function splitPlaceholders(
  text: string,
  values: Readonly<Record<string, string>>,
): ReportTextPart[] {
  const parts: ReportTextPart[] = [];
  let last = 0;
  for (const m of text.matchAll(TOKEN)) {
    const at = m.index ?? 0;
    if (at > last) parts.push({ kind: 'text', text: text.slice(last, at) });
    const key = (m[1] ?? '').trim();
    parts.push({
      kind: 'value',
      key,
      text: Object.prototype.hasOwnProperty.call(values, key) ? (values[key] ?? null) : null,
    });
    last = at + m[0].length;
  }
  if (last < text.length) parts.push({ kind: 'text', text: text.slice(last) });
  return parts;
}

/** The filled text as a string, as the PDF prints it. */
export function fillReportText(text: string, values: Readonly<Record<string, string>>): string {
  return splitPlaceholders(text, values)
    .map((p) => (p.kind === 'text' ? p.text : (p.text ?? '—')))
    .join('');
}

/** The filled text, each rollup's word marked as one (title: where it comes from). */
export function ReportText({
  text,
  values,
}: {
  text: string;
  values: Readonly<Record<string, string>>;
}) {
  return (
    <>
      {splitPlaceholders(text, values).map((p, i) =>
        p.kind === 'text' ? (
          <Fragment key={i}>{p.text}</Fragment>
        ) : p.text === null ? (
          <span
            key={i}
            data-placeholder={p.key}
            title={`No rollup answers {{${p.key}}}`}
            className="text-ink-500"
          >
            —
          </span>
        ) : (
          <span
            key={i}
            data-placeholder={p.key}
            title="From the rollups"
            className="font-semibold text-ink-900"
          >
            {p.text}
          </span>
        ),
      )}
    </>
  );
}
